import { Outlet, createFileRoute, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { AttendanceMonth } from "@/components/employees/attendance-month";
import { AttendanceHistory, AuditTrail, LeaveHistory, ShiftHistory } from "@/components/employees/history";
import { ProfileHeader } from "@/components/employees/profile-header";
import { ProfileData } from "@/components/employees/profile-data";
import { PayrollTab } from "@/components/employees/payroll-tab";
import { PerformanceTab } from "@/components/employees/performance-tab";
import { RecordRail, type RailGroup } from "@/components/employees/record-rail";
import { AdditionalFieldsSection } from "@/components/employees/sections/additional";
import { BankSection } from "@/components/employees/sections/bank";
import { JobSection } from "@/components/employees/sections/job";
import { PersonalSection } from "@/components/employees/sections/personal";
import { LeaveTypesSection } from "@/components/employees/sections/leave-types";
import { LeaveLedgerSection } from "@/components/employees/sections/leave-ledger";
import {
  AddressesSection,
  ContactsSection,
  ContractsSection,
  DocumentsSection,
  InsuranceSection,
} from "@/components/employees/sections/collections";
import { QualificationsTab } from "@/components/employees/sections/qualifications";
import { buttonVariants } from "@/components/ui/button";
import { FileLeavePanel } from "@/pages/dashboard/leave/file-panel";
import { employees as copy, employeeRecord, leave as leaveCopy } from "@/content";
import { buildQuery } from "@/utils/query-params";
import { can, scopeToSupervisorId } from "@/constants/permissions";
import { getEmployee } from "@/api/employees";
import { collectionCounts } from "@/utils/services/employee-records";
import { useSession } from "@/context/UserContext";
import { AccessDenied } from "@/components/dashboard/access-denied";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { RecordNotFound } from "@/components/dashboard/record-not-found";
import { PageError, PageLoading } from "@/components/dashboard/page-state";

/**
 * The employee record.
 *
 * Tabs follow the employee screens: data, work and contracts, personal data
 * (emergency contacts and additional fields live here), qualifications,
 * documents, bank, leave, attendance, performance, payroll, and the log.
 *
 * `$id/edit` is a child of this route. Without an `<Outlet />` when that child
 * matches, `/employees/:id/edit` kept rendering this detail page.
 */
export const Route = createFileRoute("/_main/dashboard/employees/$id")({
  component: () => (
    <RequirePermission action="employees.read">
      <EmployeeIdLayout />
    </RequirePermission>
  ),
});

function EmployeeIdLayout() {
  const showingChild = useRouterState({
    select: (state) => state.matches.some((match) => match.routeId.endsWith("/edit")),
  });
  if (showingChild) return <Outlet />;
  return <EmployeeDetailPage />;
}

const DEFAULT_TAB = "data";

const LEGACY_TABS: Record<string, string> = {
  job: "work",
  contract: "work",
  emergency: "personal",
  additional: "personal",
  shifts: "log",
  audit: "log",
  "leave-requests": "leave",
};

function EmployeeDetailPage() {
  const { user, settings } = useSession();
  const { id } = Route.useParams();
  const query = useQueryParams();
  const [filingLeave, setFilingLeave] = useState(false);
  const employeeId = Number(id);
  const validId = Number.isInteger(employeeId) && employeeId >= 1;

  const employeeQuery = useQuery({
    queryKey: ["employees", "get", employeeId],
    queryFn: () => getEmployee(employeeId),
    enabled: validId,
  });
  const countsQuery = useQuery({
    queryKey: ["employees", "collectionCounts", employeeId],
    queryFn: () => collectionCounts(employeeId),
    enabled: validId,
  });

  if (!validId || (employeeQuery.isFetched && !employeeQuery.data)) {
    return <RecordNotFound backTo="/dashboard/employees" backLabel={copy.backToList} />;
  }
  if (employeeQuery.isPending) return <PageLoading />;
  if (employeeQuery.isError || !employeeQuery.data) {
    return <PageError error={employeeQuery.error} />;
  }

  const employee = employeeQuery.data;
  const scope = scopeToSupervisorId(user);
  if (scope !== null && employee.supervisorId !== scope && employee.id !== scope) {
    return <AccessDenied role={user.role} />;
  }

  const canUpdate = can(user.role, "employees.update");
  const canWriteProfile = can(user.role, "employees.writeProfile");
  const canSeeSensitive = can(user.role, "employees.readSensitive");
  const canWriteLeaveTypes = can(user.role, "employees.writeLeaveTypes");
  const canPerformance = can(user.role, "performance.read");
  const canPayroll = can(user.role, "payroll.read");
  const canAudit = can(user.role, "audit.read");
  const canLeave = can(user.role, "leave.read");
  const canAttendance = can(user.role, "attendance.read");

  const groups: RailGroup[] = [
    {
      label: employeeRecord.groupProfile,
      entries: [
        { value: "data", label: employeeRecord.tabData },
        { value: "work", label: employeeRecord.tabWork },
        { value: "personal", label: employeeRecord.sectionPersonal },
        { value: "qualifications", label: employeeRecord.tabQualifications },
        ...(canSeeSensitive
          ? [
              {
                value: "documents",
                label: employeeRecord.sectionDocuments,
                count: countsQuery.data?.documents,
              },
            ]
          : []),
        { value: "bank", label: employeeRecord.sectionBank },
        { value: "leave", label: employeeRecord.sectionLeave },
        ...(canAttendance
          ? [{ value: "attendance", label: employeeRecord.tabAttendanceLog }]
          : []),
        ...(canPerformance
          ? [{ value: "performance", label: employeeRecord.tabPerformance }]
          : []),
        ...(canPayroll ? [{ value: "payroll", label: employeeRecord.tabPayroll }] : []),
        { value: "log", label: employeeRecord.tabLog },
      ],
    },
  ];

  const allowed = new Set(groups.flatMap((group) => group.entries.map((entry) => entry.value)));
  const requestedRaw = typeof query.tab === "string" ? query.tab : undefined;
  const requested = requestedRaw ? (LEGACY_TABS[requestedRaw] ?? requestedRaw) : undefined;
  const tab = requested && allowed.has(requested) ? requested : DEFAULT_TAB;
  const logView =
    query.view === "audit" || requestedRaw === "audit"
      ? "audit"
      : query.view === "attendance"
        ? "attendance"
        : "shifts";
  const focusDay = typeof query.day === "string" ? query.day : undefined;

  const href = (value: string) =>
    `/dashboard/employees/${employee.id}${buildQuery(query, {
      tab: value === DEFAULT_TAB ? null : value,
      month: null,
      statement: null,
      adjustments: null,
      day: null,
      view: null,
    })}`;

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <ProfileHeader
        employee={employee}
        canUpdate={canUpdate}
        canDeactivate={can(user.role, "employees.deactivate")}
        canInvite={can(user.role, "employees.invite")}
        canApprove={can(user.role, "employees.approve")}
        isSelf={employee.id === user.id}
      />

      <div className="mt-6">
        <RecordRail groups={groups} active={tab} href={href} />
      </div>

      <div className="mt-6 min-w-0">
        {tab === "data" ? <ProfileData employee={employee} canWrite={canWriteProfile} /> : null}

        {tab === "work" ? (
          <div className="flex flex-col gap-10">
            <p className="text-xs font-semibold text-n6">{employeeRecord.groupEmployment}</p>
            <h2 className="sr-only">{employeeRecord.sectionJob}</h2>
            <JobSection employee={employee} canWrite={canWriteProfile} canUpdateAccount={canUpdate} />
            <ContractsSection employeeId={employee.id} canWrite={canWriteProfile} />
            <InsuranceSection employeeId={employee.id} canWrite={canWriteProfile} />
          </div>
        ) : null}

        {tab === "personal" ? (
          <div className="flex flex-col gap-10">
            <PersonalSection
              employeeId={employee.id}
              canWrite={canWriteProfile}
              canSeeSensitive={canSeeSensitive}
            />
            <h2 className="text-sm font-bold text-n9">{employeeRecord.sectionEmergency}</h2>
            <p className="sr-only">{employeeRecord.sectionContact}</p>
            <ContactsSection
              employeeId={employee.id}
              canWrite={canWriteProfile}
              phone={employee.phone}
              email={employee.email}
              canEditAccount={canUpdate}
            />
            <AddressesSection
              employeeId={employee.id}
              canWrite={canWriteProfile}
              zones={[]}
              centre={{ lat: settings.defaultMapLat, lng: settings.defaultMapLng }}
            />
            <AdditionalFieldsSection employeeId={employee.id} canWrite={canWriteProfile} />
          </div>
        ) : null}

        {tab === "qualifications" ? (
          <QualificationsTab employeeId={employee.id} canWrite={canWriteProfile} />
        ) : null}

        {tab === "bank" ? (
          <BankSection
            employeeId={employee.id}
            canWrite={canWriteProfile}
            canSeeSensitive={canSeeSensitive}
          />
        ) : null}

        {tab === "leave" ? (
          <div className="flex flex-col gap-10">
            {can(user.role, "leave.create") ? (
              <div>
                <button type="button" className={buttonVariants()} onClick={() => setFilingLeave(true)}>
                  {leaveCopy.fileOpen}
                </button>
              </div>
            ) : null}
            {filingLeave ? (
              <FileLeavePanel
                employee={{ id: employee.id, name: employee.name, employeeNo: employee.employeeNo }}
                onClose={() => setFilingLeave(false)}
              />
            ) : null}
            <LeaveTypesSection employeeId={employee.id} canWrite={canWriteLeaveTypes} />
            {canLeave ? <LeaveLedgerSection employeeId={employee.id} /> : null}
            {canLeave ? (
              <div>
                <h2 className="mb-2 text-sm font-bold text-n9">{employeeRecord.sectionLeaveRequests}</h2>
                <LeaveHistory employeeId={employee.id} />
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === "documents" && canSeeSensitive ? (
          <DocumentsSection employeeId={employee.id} canWrite={canWriteProfile} />
        ) : null}

        {tab === "attendance" ? (
          <div>
            <h2 className="mb-2 text-sm font-bold text-n9">{employeeRecord.sectionAttendance}</h2>
            <AttendanceMonth employeeId={employee.id} focusDay={focusDay} />
          </div>
        ) : null}

        {tab === "performance" && canPerformance ? (
          <PerformanceTab employeeId={employee.id} params={query} />
        ) : null}

        {tab === "payroll" && canPayroll ? (
          <PayrollTab
            employeeId={employee.id}
            params={query}
            canWrite={can(user.role, "payroll.write")}
          />
        ) : null}

        {tab === "log" ? (
          <div className="flex flex-col gap-4">
            <h2 className="text-sm font-bold text-n9">{employeeRecord.groupHistory}</h2>
            <div className="flex gap-2">
              <Link
                to={`/dashboard/employees/${employee.id}${buildQuery(query, { tab: "log", view: null })}`}
                className={logView === "shifts" ? "text-xs font-semibold text-n9" : "text-xs font-semibold text-n6"}
              >
                {employeeRecord.tabLogShifts}
              </Link>
              <Link
                to={`/dashboard/employees/${employee.id}${buildQuery(query, { tab: "log", view: "attendance" })}`}
                className={logView === "attendance" ? "text-xs font-semibold text-n9" : "text-xs font-semibold text-n6"}
              >
                {employeeRecord.logAttendance}
              </Link>
              {canAudit ? (
                <Link
                  to={`/dashboard/employees/${employee.id}${buildQuery(query, { tab: "log", view: "audit" })}`}
                  className={logView === "audit" ? "text-xs font-semibold text-n9" : "text-xs font-semibold text-n6"}
                >
                  {employeeRecord.tabLogChanges}
                </Link>
              ) : null}
            </div>
            {logView === "audit" && canAudit ? (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-n8">{employeeRecord.sectionAudit}</h3>
                <AuditTrail employeeId={employee.id} />
              </div>
            ) : logView === "attendance" ? (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-n8">{employeeRecord.sectionAttendance}</h3>
                <AttendanceHistory employeeId={employee.id} focusDay={focusDay} />
              </div>
            ) : (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-n8">{employeeRecord.sectionShifts}</h3>
                <ShiftHistory employeeId={employee.id} />
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
